import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const MobileAppRedirect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const [isRedirecting, setIsRedirecting] = useState(true);

  // Get encrypted string from URL params
  const encryptedString =
    searchParams.get('token') || searchParams.get('data') || '';

  // App store URLs
  const APP_STORE_URL = 'https://apps.apple.com/by/app/konvoy/id6742449125'; // Replace with actual App Store URL
  const PLAY_STORE_URL =
    'https://play.google.com/store/apps/details?id=com.uno.driver'; // Replace with actual Play Store URL

  // Detect user's operating system
  const detectOS = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'iOS';
    }

    if (/android/i.test(userAgent)) {
      return 'Android';
    }

    return 'Desktop';
  };

  // Check if mobile app is installed and redirect
  const tryDeepLink = () => {
    // Validate encrypted string
    if (!encryptedString || encryptedString.trim() === '') {
      console.log('No encrypted string provided, redirecting to app store');
      redirectToAppStore();
      return;
    }

    // Properly encode the encrypted string to handle special characters
    const encodedString = encodeURIComponent(encryptedString);
    const deepLinkURL = `unodriver://auth/${encodedString}`;
    const os = detectOS();

    console.log('Attempting deep link:', deepLinkURL);

    // Store the current time to detect if we left the page
    const startTime = Date.now();
    let hasSwitched = false;

    // Function to handle app store fallback
    const fallbackToStore = () => {
      if (!hasSwitched) {
        redirectToAppStore();
      }
    };

    // Different approaches for iOS and Android
    if (os === 'iOS') {
      // For iOS, use a more direct approach
      // Try to open the deep link directly
      window.location.href = deepLinkURL;

      // Set a timeout for fallback - iOS typically takes 1-2 seconds
      setTimeout(() => {
        // Check if we're still on the page after attempting deep link
        const timeElapsed = Date.now() - startTime;
        if (timeElapsed >= 1500 && !document.hidden) {
          fallbackToStore();
        }
      }, 1500);
    } else if (os === 'Android') {
      // For Android, try intent URL first, then custom scheme
      const intentURL = `intent://auth/${encodedString}#Intent;scheme=unodriver;package=com.uno.driver;end`;

      try {
        // Try the intent URL first (more reliable on Android)
        window.location.href = intentURL;

        // Fallback timeout for Android
        setTimeout(() => {
          const timeElapsed = Date.now() - startTime;
          if (timeElapsed >= 2000 && !document.hidden) {
            // If intent failed, try custom scheme
            window.location.href = deepLinkURL;

            // Final fallback to store
            setTimeout(() => {
              if (!document.hidden) {
                fallbackToStore();
              }
            }, 1500);
          }
        }, 500);
      } catch (error) {
        // If intent URL fails, try custom scheme
        console.log('Intent URL failed, trying custom scheme');
        window.location.href = deepLinkURL;

        setTimeout(() => {
          fallbackToStore();
        }, 2000);
      }
    }

    // Enhanced visibility change detection
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hasSwitched = true;
        setIsRedirecting(false);
        // User likely switched to the app
        console.log('App likely opened - user switched away from browser');
      }
    };

    // Enhanced focus/blur detection for better app detection
    const handleBlur = () => {
      hasSwitched = true;
      setIsRedirecting(false);
      console.log('App likely opened - window lost focus');
    };

    const handleFocus = () => {
      // If we regain focus quickly, the app probably didn't open
      setTimeout(() => {
        if (!hasSwitched && Date.now() - startTime > 3000) {
          fallbackToStore();
        }
      }, 100);
    };

    // Add multiple event listeners for better detection
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    // Cleanup event listeners
    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    }, 10000);
  };

  // Redirect to appropriate app store
  const redirectToAppStore = () => {
    const os = detectOS();
    let storeURL;

    switch (os) {
      case 'iOS':
        storeURL = APP_STORE_URL;
        break;
      case 'Android':
        storeURL = PLAY_STORE_URL;
        break;
      default:
        // For desktop users, show a message or redirect to web app
        setIsRedirecting(false);
        return;
    }

    window.location.href = storeURL;
  };

  // Handle manual app store redirect
  const handleManualRedirect = () => {
    redirectToAppStore();
  };

  // Handle back to web app
  const handleBackToWeb = () => {
    navigate('/');
  };

  useEffect(() => {
    const os = detectOS();

    // Only attempt deep link on mobile devices
    if (os === 'iOS' || os === 'Android') {
      // Small delay to ensure page is loaded
      setTimeout(() => {
        tryDeepLink();
      }, 500);

      // Countdown timer
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    } else {
      // For desktop users, don't attempt deep link
      setIsRedirecting(false);
    }
  }, [encryptedString]);

  const os = detectOS();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">KONVOY</h1>
          <p className="text-gray-600">Mobil uygulamaya yönlendiriliyor...</p>
        </div>

        {os === 'Desktop' ? (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                Bu özellik mobil cihazlar için tasarlanmıştır. Lütfen mobil
                cihazınızdan erişin.
              </p>
            </div>
          </div>
        ) : isRedirecting ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-700">Uygulama açılıyor...</span>
            </div>

            {countdown > 0 && (
              <p className="text-sm text-gray-500">
                {countdown} saniye sonra uygulama mağazasına
                yönlendirileceksiniz
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 text-sm">
                Uygulama bulunamadı veya açılamadı.
              </p>
            </div>

            <div className="space-y-2">
              <button
                onClick={handleManualRedirect}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                {os === 'iOS' ? "App Store'dan İndir" : "Play Store'dan İndir"}
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            KONVOY © 2024. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MobileAppRedirect;
